import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { api } from '../../utils/api';
import MultiSelect from '../MultiSelect';

// Settings → Territories. Who owns which HQ countries — the brand's
// headquarters is where procurement and R&D sit, so it is the address a
// rep actually works, and coverage hangs off the country rather than
// off the brand. Assign a country once and every brand discovered in it
// afterwards routes itself.
//
// The table is deliberately flat: one row per territory, edited in
// place, plus an Unassigned row that can't be edited away. That last
// row is the point of the screen — it names the countries carrying
// brands nobody is working yet.

const STROKE = 2.75;

function countryList(countries) {
  if (!countries.length) return 'No countries yet';
  if (countries.length <= 3) return countries.join(', ');
  return `${countries.slice(0, 3).join(', ')} +${countries.length - 3} more`;
}

function ownerLabel(territory) {
  if (territory.owner_name && territory.owner_email) {
    return `${territory.owner_name} · ${territory.owner_email}`;
  }
  return territory.owner_name || territory.owner_email || 'Nobody yet';
}

// The owner picker is a datalist rather than a <select>: colleagues who
// have signed in are offered as suggestions, but a territory has to be
// assignable to someone starting on Monday, whose account doesn't exist
// yet.
function OwnerField({ value, members, onChange, id }) {
  return (
    <>
      <input
        id={id}
        className="input"
        list={`${id}-members`}
        type="email"
        placeholder="colleague@company.com"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
      <datalist id={`${id}-members`}>
        {members.map((m) => (
          <option key={m.email} value={m.email}>{m.name}</option>
        ))}
      </datalist>
    </>
  );
}

function TerritoryRow({
  territory, members, countryOptions, onSave, onDelete, busy,
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(territory);
  const [error, setError] = useState(null);

  // Reopening after a save (or someone else's edit landing in a
  // refresh) must show what's stored, not a stale draft.
  useEffect(() => { setDraft(territory); setError(null); }, [territory, open]);

  const dirty = (
    draft.name !== territory.name
    || (draft.owner_email || '') !== (territory.owner_email || '')
    || (draft.owner_name || '') !== (territory.owner_name || '')
    || draft.countries.join(',') !== territory.countries.join(',')
  );

  async function save() {
    setError(null);
    try {
      await onSave(territory.id, {
        name: draft.name,
        owner_email: draft.owner_email || '',
        owner_name: draft.owner_name || '',
        countries: draft.countries,
      });
      setOpen(false);
    } catch (e) {
      // A country claimed by another territory comes back as a 409
      // naming the holder — shown inline, because the fix is over there.
      setError(e.message || 'Failed to save territory');
    }
  }

  return (
    <>
      <tr>
        <td style={{ fontWeight: 600 }}>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block', width: 10, height: 10, borderRadius: 999,
              background: territory.color, marginRight: 8,
            }}
          />
          {territory.name}
        </td>
        <td style={{ opacity: territory.owner_email ? 1 : 0.6 }}>
          {ownerLabel(territory)}
        </td>
        <td title={territory.countries.join(', ')}>
          {countryList(territory.countries)}
        </td>
        <td style={{ textAlign: 'right' }}>{territory.brand_count}</td>
        <td style={{ textAlign: 'right' }}>
          <button
            type="button"
            className="btn btn-ghost"
            style={{ fontSize: 12 }}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Close' : 'Edit'}
          </button>
        </td>
      </tr>

      {open && (
        <tr>
          <td colSpan={5} style={{ background: 'var(--background)' }}>
            <div style={{ display: 'grid', gap: 'var(--space-3)', maxWidth: 620 }}>
              <div className="field">
                <label htmlFor={`t-name-${territory.id}`}>Name</label>
                <input
                  id={`t-name-${territory.id}`}
                  className="input"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </div>

              <div className="field" style={{ display: 'grid', gap: 'var(--space-2)' }}>
                <label htmlFor={`t-owner-name-${territory.id}`}>Owner</label>
                <input
                  id={`t-owner-name-${territory.id}`}
                  className="input"
                  placeholder="Name"
                  value={draft.owner_name || ''}
                  onChange={(e) => setDraft({ ...draft, owner_name: e.target.value })}
                />
                <OwnerField
                  id={`t-owner-email-${territory.id}`}
                  value={draft.owner_email}
                  members={members}
                  onChange={(v) => setDraft({ ...draft, owner_email: v })}
                />
              </div>

              <div className="field" style={{ display: 'grid', gap: 'var(--space-2)' }}>
                <label>HQ countries</label>
                <MultiSelect
                  options={countryOptions}
                  selected={draft.countries}
                  onChange={(countries) => setDraft({ ...draft, countries })}
                  allLabel="No countries yet"
                  summaryNoun="countries"
                  searchPlaceholder="Search countries…"
                  ariaLabel="HQ countries in this territory"
                />
                <p style={{ margin: 0, fontSize: 12, opacity: 0.65 }}>
                  A country belongs to one territory at a time. Take it from
                  its current owner before assigning it here.
                </p>
              </div>

              {error && (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--color-accent-700)' }}>{error}</p>
              )}

              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ fontSize: 13 }}
                  disabled={busy || !dirty || !draft.name.trim()}
                  onClick={save}
                >
                  {busy ? <Loader2 size={14} strokeWidth={STROKE} className="spin" /> : null}
                  Save changes
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ fontSize: 13 }}
                  disabled={busy}
                  onClick={() => onDelete(territory)}
                >
                  <Trash2 size={14} strokeWidth={STROKE} /> Delete
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function TerritoriesTab() {
  const [territories, setTerritories] = useState([]);
  const [unassigned, setUnassigned] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, memberData] = await Promise.all([
        api.listTerritories(),
        api.listTerritoryMembers().catch(() => ({ members: [] })),
      ]);
      setTerritories(data.territories || []);
      setUnassigned(data.unassigned || null);
      setCountryOptions(data.country_options || []);
      setMembers(memberData.members || []);
    } catch (e) {
      setError(e.message || 'Failed to load territories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(territoryId, fields) {
    setBusyId(territoryId);
    try {
      await api.updateTerritory(territoryId, fields);
      await load();
    } finally {
      setBusyId(null);
    }
  }

  async function create() {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      await api.createTerritory({ name, countries: [] });
      setNewName('');
      await load();
    } catch (e) {
      setError(e.message || 'Failed to create territory');
    } finally {
      setCreating(false);
    }
  }

  async function remove(territory) {
    // Deleting frees the countries rather than touching any brand, so
    // the confirm says where they go — nothing is lost, but the brands
    // do stop being anybody's.
    const message = territory.countries.length
      ? `Delete "${territory.name}"? Its ${territory.countries.length} `
        + `countr${territory.countries.length === 1 ? 'y' : 'ies'} go back to Unassigned.`
      : `Delete "${territory.name}"?`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(message)) return;
    setBusyId(territory.id);
    setError(null);
    try {
      await api.deleteTerritory(territory.id);
      await load();
    } catch (e) {
      setError(e.message || 'Failed to delete territory');
    } finally {
      setBusyId(null);
    }
  }

  const assignedCount = useMemo(
    () => territories.reduce((sum, t) => sum + t.countries.length, 0),
    [territories],
  );

  return (
    <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
      <div>
        <h4 className="o-card-head-title">Territories</h4>
        <p className="o-card-head-sub">
          Who works which HQ countries. Headquarters is where procurement and
          R&amp;D sit, so a brand routes to whoever owns its HQ country — and a
          brand discovered next week routes itself. Movers and Brands can both
          be cut this way.
        </p>
      </div>

      {error && (
        <p style={{ margin: 0, fontSize: 14, color: 'var(--color-accent-700)' }}>Error: {error}</p>
      )}

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, opacity: 0.75 }}>
          <Loader2 size={16} strokeWidth={STROKE} className="spin" /> Loading territories…
        </div>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Territory</th>
                <th>Owner</th>
                <th>HQ countries</th>
                <th style={{ textAlign: 'right' }}>Brands</th>
                <th aria-label="Edit" />
              </tr>
            </thead>
            <tbody>
              {territories.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', opacity: 0.65, padding: 'var(--space-4)' }}>
                    No territories yet — every brand reads as unassigned.
                  </td>
                </tr>
              )}
              {territories.map((territory) => (
                <TerritoryRow
                  key={territory.id}
                  territory={territory}
                  members={members}
                  countryOptions={countryOptions}
                  busy={busyId === territory.id}
                  onSave={save}
                  onDelete={remove}
                />
              ))}

              {unassigned && (
                <tr>
                  <td style={{ fontWeight: 600, opacity: 0.7 }}>Unassigned</td>
                  <td style={{ opacity: 0.6 }}>Nobody</td>
                  <td
                    style={{ opacity: 0.7 }}
                    title={unassigned.countries.join(', ')}
                  >
                    {unassigned.countries.length
                      ? countryList(unassigned.countries)
                      : 'Every country is covered'}
                  </td>
                  <td style={{ textAlign: 'right', opacity: 0.7 }}>{unassigned.brand_count}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>

          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="input"
              style={{ maxWidth: 260 }}
              placeholder="New territory (e.g. Canada + Europe)"
              value={newName}
              aria-label="New territory name"
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') create(); }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 13 }}
              disabled={creating || !newName.trim()}
              onClick={create}
            >
              {creating
                ? <Loader2 size={14} strokeWidth={STROKE} className="spin" />
                : <Plus size={14} strokeWidth={STROKE} />}
              Add territory
            </button>
            <span style={{ fontSize: 12, opacity: 0.6 }}>
              {assignedCount} countr{assignedCount === 1 ? 'y' : 'ies'} assigned
              {unassigned?.countries.length
                ? ` · ${unassigned.countries.length} with brands still unassigned`
                : ''}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

export default TerritoriesTab;
